import { Link } from "@tanstack/react-router";
import { Menu, X } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { BrandLogo } from "@/components/brand/BrandLogo";
import { Button } from "@/components/ui/button";
import { COMPANY, COMPANY_LEGAL_FOOTER, SOCIAL, WHATSAPP_URL } from "@/lib/company";
import { canKeepMotionChoice, switchMotion } from "@/lib/motion";
import { cn } from "@/lib/utils";

type Tone = "paper" | "ink";

const NAV = [
  { href: "/#services", label: "Services" },
  { href: "/meta-ads-pricing", label: "Pricing" },
  { href: "/listing-creative-pack", label: "Creative Pack" },
  { href: "/#work", label: "Work" },
  { href: "/about", label: "About" },
  { href: "/guides", label: "Guides" },
];

/**
 * The header every public page shares.
 *
 * Each page used to carry its own copy, and they drifted: the pricing page
 * had a small logo and different links from the homepage, so moving between
 * them looked like moving between two sites. One component now, so a link
 * added here appears everywhere.
 *
 * It takes the tone of the page behind it. A section marked data-tone="ink"
 * turns the header dark while it is under it, and anything else leaves it on
 * paper, so the header never sits as a pale bar across a dark page head.
 * `tone` is what the page opens on, so the first paint is already right;
 * `overlay` lays the header over the page instead of above it, for the
 * homepage stage.
 */
export function SiteHeader({
  tone = "paper",
  overlay = false,
}: {
  tone?: Tone;
  overlay?: boolean;
}) {
  const header = useRef<HTMLElement>(null);
  const [current, setCurrent] = useState({ tone, clear: overlay && tone === "ink" });
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const read = () => {
      const node = header.current;
      if (!node) return;
      // Laid over the page, the header takes the tone of what is behind it;
      // in the flow, of what follows it.
      const probe = overlay ? node.offsetHeight / 2 : node.getBoundingClientRect().bottom + 1;
      const toned = document.querySelectorAll<HTMLElement>("[data-tone]");
      let next: Tone = "paper";
      let opening = false;
      for (let index = 0; index < toned.length; index++) {
        const box = toned[index]!.getBoundingClientRect();
        if (box.top <= probe && box.bottom > probe) {
          next = toned[index]!.getAttribute("data-tone") === "ink" ? "ink" : "paper";
          opening = index === 0;
          break;
        }
      }
      // At the very top of the homepage there is nothing to separate the
      // header from, so it carries no background at all. Once anything
      // scrolls under it, it needs its ground back.
      const clear = overlay && opening && next === "ink" && window.scrollY < 24;
      setCurrent((previous) =>
        previous.tone === next && previous.clear === clear ? previous : { tone: next, clear },
      );
    };
    read();
    window.addEventListener("scroll", read, { passive: true });
    window.addEventListener("resize", read);
    return () => {
      window.removeEventListener("scroll", read);
      window.removeEventListener("resize", read);
    };
  }, [overlay]);

  return (
    <header
      ref={header}
      className={cn(
        "site-header",
        overlay && "site-header--overlay",
        current.tone === "ink" && "tone-ink",
        current.clear && !menuOpen && "site-header--clear",
      )}
    >
      <div className="mx-auto flex h-[4.75rem] max-w-7xl items-center justify-between gap-5 px-5 lg:px-8">
        <BrandLogo />
        <nav
          aria-label="Primary"
          className="hidden items-center gap-8 whitespace-nowrap text-sm text-muted-foreground lg:flex"
        >
          {NAV.map((item) => (
            <a key={item.label} href={item.href} className="site-nav-link">
              {item.label}
            </a>
          ))}
          <Link
            to="/meta-ads-pricing"
            hash="pay"
            className="site-nav-link font-medium text-primary"
          >
            Pay online
          </Link>
        </nav>
        <div className="flex items-center gap-2">
          <Button variant="brand" size="sm" className="h-10 px-4 text-sm" asChild>
            <a href="/#audit">Free review</a>
          </Button>
          <button
            type="button"
            className="inline-flex size-10 items-center justify-center border border-border text-foreground lg:hidden"
            aria-expanded={menuOpen}
            aria-controls="site-menu"
            aria-label={menuOpen ? "Close the menu" : "Open the menu"}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X className="size-4" /> : <Menu className="size-4" />}
          </button>
        </div>
      </div>

      {/* A phone had no way to reach Pricing, About or the guides except the footer.
          Tablets use this menu too: with seven links the row needs 835px, more
          than a tablet held upright has. */}
      <nav
        id="site-menu"
        aria-label="Menu"
        hidden={!menuOpen}
        className="border-t border-border bg-background px-5 pb-5 pt-2 lg:hidden"
      >
        <ul className="divide-y divide-border">
          {NAV.map((item) => (
            <li key={item.label}>
              <a
                href={item.href}
                className="block py-3.5 font-display text-2xl"
                onClick={() => setMenuOpen(false)}
              >
                {item.label}
              </a>
            </li>
          ))}
          <li>
            <Link
              to="/meta-ads-pricing"
              hash="pay"
              className="block py-3.5 font-display text-2xl text-primary"
              onClick={() => setMenuOpen(false)}
            >
              Pay online
            </Link>
          </li>
        </ul>
      </nav>
    </header>
  );
}

const external = { target: "_blank", rel: "noopener noreferrer" } as const;

/**
 * The footer every public page shares: who Adkiro is, where everything is,
 * and the registered name. Both forms of the legal name stay here because
 * Meta's business verification looks for the Cyrillic one on the site; see
 * COMPANY_LEGAL_FOOTER.
 */
export function SiteFooter() {
  return (
    <footer data-tone="ink" className="tone-ink border-t border-border">
      <div className="mx-auto max-w-7xl px-5 pb-10 pt-14 lg:px-8">
        <div className="grid gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div>
            <BrandLogo />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-muted-foreground">
              Founder-led Meta advertising for property agencies, brokers and developers in Dubai,
              Europe and the United States.
            </p>
          </div>

          <FooterColumn title="Services">
            <Link to="/meta-ads-pricing">Services and pricing</Link>
            <a href="/meta-ads-audit">Account review</a>
            <a href="/real-estate-lead-generation-meta-ads">Property campaigns</a>
            <Link to="/listing-creative-pack">Listing Creative Pack</Link>
            <a href="/meta-pixel-conversions-api">Pixel and Conversions API</a>
            <Link to="/meta-ads-pricing" hash="pay">
              Pay online
            </Link>
          </FooterColumn>

          <FooterColumn title="Adkiro">
            <a href="/about">About Kristian</a>
            <a href="/guides">Guides</a>
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
          </FooterColumn>

          <FooterColumn title="Write to us">
            <a href="/#audit">Free review</a>
            <a href={WHATSAPP_URL} {...external}>
              WhatsApp
            </a>
            <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
            <a href={SOCIAL.instagram} {...external}>
              Instagram
            </a>
            <a href={SOCIAL.facebook} {...external}>
              Facebook
            </a>
            <a href={SOCIAL.linkedin} {...external}>
              LinkedIn
            </a>
          </FooterColumn>
        </div>

        <p className="mt-12 border-t border-border pt-6 text-xs leading-relaxed text-muted-foreground">
          © {new Date().getFullYear()} Adkiro · {COMPANY_LEGAL_FOOTER}, Sofia ·{" "}
          <a
            href="/terms#legal-notice"
            className="underline-offset-4 hover:text-foreground hover:underline"
          >
            Legal notice
          </a>
          <MotionSwitch />
        </p>
      </div>
    </footer>
  );
}

/**
 * Lets any visitor turn the site's animation off, or back on, from every
 * page. The homepage stage moves for as long as it is on screen, and a page
 * that moves on its own owes its visitor a way to stop it.
 *
 * Rendered only once the page is running: which way the switch points is
 * known in the browser, not on the server. Not rendered at all where the
 * browser will not keep the choice, since the switch works by keeping it.
 */
function MotionSwitch() {
  const [animated, setAnimated] = useState<boolean | null>(null);

  useEffect(() => {
    if (!canKeepMotionChoice()) return;
    setAnimated(document.documentElement.classList.contains("motion-ok"));
  }, []);

  if (animated === null) return null;

  return (
    <>
      {" · "}
      <button
        type="button"
        className="cursor-pointer underline-offset-4 hover:text-foreground hover:underline"
        onClick={() => {
          if (!switchMotion(animated ? "still" : "play")) setAnimated(null);
        }}
      >
        {animated ? "Turn animation off" : "Turn animation on"}
      </button>
    </>
  );
}

function FooterColumn({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-[0.68rem] font-medium uppercase tracking-[0.22em] text-muted-foreground">
        {title}
      </p>
      <div className="mt-4 flex flex-col items-start gap-2.5 text-sm text-foreground/85 [&>a:hover]:text-primary">
        {children}
      </div>
    </div>
  );
}
